import { Component, OnInit, inject } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCard, MatCardTitle } from '@angular/material/card';
import { MatFormField, MatLabel, MatError, MatHint } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { GameService } from '../../services/game.service';
import { GamePlayService } from '../../services/game-play.service';
import { MessageDialogService } from '../../services/message-dialog.service';
import { DialogSelectPlayerComponent } from '../dialog-select-player/dialog-select-player.component';
import { GamePlay } from '../../model/game-play';
import { ODK } from '../../model/odk';
import { Down } from '../../model/down';
import { HashSimple } from '../../model/hash';
import { PlayType } from '../../model/playType';
import { PlayResult } from '../../model/playResult';
import { PenTeam } from '../../model/penTeam';
import { GameStatus } from '../../model/gameStatus';
import { captureExceptionWithTags, captureMessageWithTags } from '../../../sentry/sentry';
import { FilterOptionsPipe } from '../../pipe/filter-options.pipe';

// Vérification allégée (voir isFormValid()) : le joueur "principal" attendu pour
// un type de jeu donné, tel que requis par le validation() de la page de saisie
// en direct correspondante (run.component.ts#rusher, incomplete-pass/complete-pass/
// interception.component.ts#passer, punt/kickoff/fg-attempt.component.ts#kicker).
// Ne reproduit pas les règles conditionnelles au résultat (ex: receveur requis
// seulement si Complete) — seulement le champ toujours obligatoire pour le type.
const PRIMARY_PLAYER_FIELD_BY_PLAY_TYPE: Partial<Record<PlayType, 'rusher' | 'passer' | 'kicker'>> = {
  [PlayType.Run]: 'rusher',
  [PlayType.Pass]: 'passer',
  [PlayType.Punt]: 'kicker',
  [PlayType.PuntRec]: 'kicker',
  [PlayType.KO]: 'kicker',
  [PlayType.KOREC]: 'kicker',
  [PlayType.OnsideKick]: 'kicker',
  [PlayType.OnsideKickRec]: 'kicker',
  [PlayType.FG]: 'kicker',
  [PlayType.FGBlock]: 'kicker',
  [PlayType.ExtraPt]: 'kicker',
  [PlayType.ExtraPtBlock]: 'kicker',
};

const REQUIRED_PLAYER_MESSAGE_KEY: Record<'rusher' | 'passer' | 'kicker', string> = {
  rusher: 'validation.gamePlay.rusherRequired',
  passer: 'validation.gamePlay.passerRequired',
  kicker: 'validation.gamePlay.kickerRequired',
};

const KICKING_PLAY_TYPES: PlayType[] = [
  PlayType.Punt, PlayType.PuntRec,
  PlayType.KO, PlayType.KOREC,
  PlayType.OnsideKick, PlayType.OnsideKickRec,
  PlayType.FG, PlayType.FGBlock,
  PlayType.ExtraPt, PlayType.ExtraPtBlock,
];

// Playtypes où c'est l'équipe qui REÇOIT le botté qui est concernée (voir
// SetKickerCommand.ts#playTypeDeffensif) : le champ à valider est alors
// oppKicker plutôt que kicker.
const KICK_RECEIVING_PLAY_TYPES: PlayType[] = [
  PlayType.KOREC, PlayType.OnsideKickRec, PlayType.PuntRec,
  PlayType.ExtraPtBlock, PlayType.FGBlock,
];

// Seuls FG/FGBlock/ExtraPt/ExtraPtBlock n'ont pas de plaqueur (fg-attempt.component.html,
// pat.component.html) : KO, Punt et Onside Kick affichent tackler1/2 de façon
// permanente, peu importe le résultat (kickoff/punt/onside-kick.component.html).
const NO_TACKLE_PLAY_TYPES: PlayType[] = [
  PlayType.FG, PlayType.FGBlock, PlayType.ExtraPt, PlayType.ExtraPtBlock,
];

// Résultats où personne n'est plaqué : botté/converti réussi (aucun retour),
// ou passe incomplète (le ballon n'est jamais rattrapé). Simple/DeadBall/
// TouchBack/TeamLost gardent volontairement les plaqueurs : kickoff/punt/
// team-lost.component.html les affichent peu importe le résultat choisi.
const NO_TACKLE_RESULTS: PlayResult[] = [
  PlayResult.Good, PlayResult.Incomplete, PlayResult.IncompleteBattedDown,
];

const COMPLETE_PASS_RESULTS: PlayResult[] = [
  PlayResult.Complete, PlayResult.CompleteTD, PlayResult.CompleteFumble,
];

// KO/Punt/Onside Kick affichent le retourneur en permanence (kickoff.component.html);
// FG/Extra Pt seulement si le botté a raté (fg-attempt.component.html#showNoGoodItem).
const RETURNER_ALWAYS_PLAY_TYPES: PlayType[] = [
  PlayType.KO, PlayType.KOREC,
  PlayType.OnsideKick, PlayType.OnsideKickRec,
  PlayType.Punt, PlayType.PuntRec,
];
const RETURNER_UNLESS_GOOD_PLAY_TYPES: PlayType[] = [
  PlayType.FG, PlayType.FGBlock, PlayType.ExtraPt, PlayType.ExtraPtBlock,
];

type PlayerRoleKey = 'rusher' | 'passer' | 'receiver' | 'kicker' | 'returner'
  | 'tackler1' | 'tackler2' | 'battedDown' | 'recoveredBy' | 'forcedFumbleBy' | 'interceptedBy';

// Détermine, pour un rôle donné, s'il est pertinent selon le Play Type/Résultat
// choisi — pour ne pas afficher 11 champs joueurs quand seuls 2-3 s'appliquent
// au jeu en cours. Vérifié champ par champ contre les 13 pages de saisie en
// direct (game-play/*/*.component.html) plutôt que déduit :
// - qb-sack.component.html ne capture jamais de passeur (seulement le plaqueur) ;
// - onside-kick.component.html étiquette "recoveredBy" mais met à jour le champ
//   modèle `returner` (onChangeReturner) — recoveredBy (le vrai champ) n'est
//   utilisé que par fumble.component.ts.
const ROLE_VISIBILITY: Record<PlayerRoleKey, (playType: PlayType, result: PlayResult) => boolean> = {
  rusher: playType => playType === PlayType.Run || playType === PlayType['2Pt'] || playType === PlayType['2PtDefend'],
  passer: (playType, result) => (playType === PlayType.Pass && result !== PlayResult.Sack && result !== PlayResult.SackFumble && result !== PlayResult.SackFumbleDefTD)
    || playType === PlayType['2Pt'] || playType === PlayType['2PtDefend'],
  receiver: (playType, result) => (playType === PlayType.Pass && COMPLETE_PASS_RESULTS.includes(result))
    || playType === PlayType['2Pt'] || playType === PlayType['2PtDefend'],
  kicker: playType => KICKING_PLAY_TYPES.includes(playType),
  returner: (playType, result) => {
    if (RETURNER_ALWAYS_PLAY_TYPES.includes(playType)) return true;
    if (RETURNER_UNLESS_GOOD_PLAY_TYPES.includes(playType)) return result !== PlayResult.Good;
    return false;
  },
  tackler1: (playType, result) => !NO_TACKLE_PLAY_TYPES.includes(playType) && !NO_TACKLE_RESULTS.includes(result),
  tackler2: (playType, result) => !NO_TACKLE_PLAY_TYPES.includes(playType) && !NO_TACKLE_RESULTS.includes(result),
  battedDown: (_playType, result) => result === PlayResult.Incomplete || result === PlayResult.IncompleteBattedDown,
  recoveredBy: (_playType, result) => !!result?.includes('Fumble'),
  forcedFumbleBy: (_playType, result) => !!result?.includes('Fumble'),
  interceptedBy: (_playType, result) => !!result?.includes('Interception'),
};

interface PlayerRole {
  key: PlayerRoleKey;
  oppKey: string;
  i18nKey: string;
}

// Un rôle par ligne de la grille Home/Away (voir play-list.component.ts#roleLabels/
// homeKeys/awayKeys, qui utilise la même liste de champs pour le tableau).
const PLAYER_ROLES: PlayerRole[] = [
  { key: 'rusher', oppKey: 'oppRusher', i18nKey: 'rusher' },
  { key: 'passer', oppKey: 'oppPasser', i18nKey: 'passer' },
  { key: 'receiver', oppKey: 'oppReceiver', i18nKey: 'receiver' },
  { key: 'kicker', oppKey: 'oppKicker', i18nKey: 'kicker' },
  { key: 'returner', oppKey: 'oppReturner', i18nKey: 'returner' },
  { key: 'tackler1', oppKey: 'oppTackler1', i18nKey: 'tackler1' },
  { key: 'tackler2', oppKey: 'oppTackler2', i18nKey: 'tackler2' },
  { key: 'battedDown', oppKey: 'oppBattedDown', i18nKey: 'battedDown' },
  { key: 'recoveredBy', oppKey: 'oppRecoveredBy', i18nKey: 'recoveredBy' },
  { key: 'forcedFumbleBy', oppKey: 'oppForcedFumbleBy', i18nKey: 'forceFumbleBy' },
  { key: 'interceptedBy', oppKey: 'oppInterceptedBy', i18nKey: 'interceptedBy' },
];

@Component({
    selector: 'app-play-list-edit',
    templateUrl: './play-list-edit.component.html',
    styleUrls: ['./play-list-edit.component.scss'],
    imports: [
      FormsModule, ReactiveFormsModule,
      MatCard, MatCardTitle, MatFormField, MatLabel, MatError, MatHint,
      MatSelect, MatOption, MatInput, MatButton, MatIconButton, MatIcon,
      TranslocoModule, FilterOptionsPipe
    ]
})
export class PlayListEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private gamePlayService = inject(GamePlayService);
  private _snackBar = inject(MatSnackBar);
  private translocoService = inject(TranslocoService);
  private messageDialog = inject(MessageDialogService);
  private dialog = inject(MatDialog);
  game = inject(GameService);

  playerRoles = PLAYER_ROLES;

  gamePlayNumber!: number;
  editedGamePlay?: GamePlay;
  gamePlayForm?: FormGroup;
  newPlay = false;

  odkEnum: any = ODK;
  odkKeys = Object.keys(ODK).filter(key => isNaN(Number(key)));

  hashEnum: any = HashSimple;
  hashKeys = Object.keys(HashSimple).filter(key => isNaN(Number(key)));

  qtrEnum = [1, 2, 3, 4, 5];

  playTypeEnum: any = PlayType;
  playResultEnum: any = PlayResult;

  penTeamEnum: any = PenTeam;
  penTeamKeys = Object.keys(PenTeam).filter(key => isNaN(Number(key)));

  ngOnInit() {
    if (this.game.game?.gameStatus === GameStatus.Completed) {
      this.back();
      return;
    }

    const gamePlayNumberParam = this.getParamGamePlayID();

    // Un "b" en suffixe indique l'insertion d'un nouveau jeu après ce gamePlayNumber
    // (voir play-list.component.ts#insertAfter).
    if (gamePlayNumberParam.toString().includes('b')) {
      this.newPlay = true;
      this.gamePlayNumber = parseInt(gamePlayNumberParam.toString().replace('b', ''));
    } else {
      this.gamePlayNumber = parseInt(gamePlayNumberParam);
    }

    this.editedGamePlay = this.game.gamePlayList.find(gamePlay => gamePlay.gamePlayNumber === this.gamePlayNumber);
    if (!this.editedGamePlay) {
      captureMessageWithTags(
        `Impossible d'éditer le gamePlay. gamePlayNumber "${this.gamePlayNumber}" non trouvé.`,
        { gameId: this.game.game?.id, gamePlayNumber: this.gamePlayNumber }
      );
      this.messageDialog.openMessage(
        'validation.warningTitle',
        'validation.warningGamePlayNotFound',
        'validation.continue',
        'warning'
      );
      this.back();
      return;
    }

    if (this.newPlay) {
      this.gamePlayForm = new FormGroup({
        odk: new FormControl(ODK.Others, [Validators.required, this.isInODK]),
        down: new FormControl(null, this.isInDown),
        distance: new FormControl(null, [Validators.required]),
        yardLine: new FormControl('', [Validators.required]),
        hash: new FormControl(this.editedGamePlay.hash, [Validators.required, this.isInHash]),
        qtr: new FormControl(this.editedGamePlay.qtr, [Validators.required]),
        playType: new FormControl(PlayType.Missing, [Validators.required, this.isInPlayType]),
        result: new FormControl(PlayResult.Missing, [Validators.required, this.isInPlayResult]),
        gnLs: new FormControl(null),
        penTeam: new FormControl(null),
        penYards: new FormControl(null),
        kickYards: new FormControl(null),
        returnYards: new FormControl(null),
        rusher: new FormControl(null, [Validators.max(99)]),
        passer: new FormControl(null, [Validators.max(99)]),
        receiver: new FormControl(null, [Validators.max(99)]),
        tackler1: new FormControl(null, [Validators.max(99)]),
        tackler2: new FormControl(null, [Validators.max(99)]),
        battedDown: new FormControl(null, [Validators.max(99)]),
        forcedFumbleBy: new FormControl(null, [Validators.max(99)]),
        returner: new FormControl(null, [Validators.max(99)]),
        kicker: new FormControl(null, [Validators.max(99)]),
        recoveredBy: new FormControl(null, [Validators.max(99)]),
        interceptedBy: new FormControl(null, [Validators.max(99)]),
        oppRusher: new FormControl(null, [Validators.max(99)]),
        oppPasser: new FormControl(null, [Validators.max(99)]),
        oppReceiver: new FormControl(null, [Validators.max(99)]),
        oppTackler1: new FormControl(null, [Validators.max(99)]),
        oppTackler2: new FormControl(null, [Validators.max(99)]),
        oppBattedDown: new FormControl(null, [Validators.max(99)]),
        oppForcedFumbleBy: new FormControl(null, [Validators.max(99)]),
        oppReturner: new FormControl(null, [Validators.max(99)]),
        oppKicker: new FormControl(null, [Validators.max(99)]),
        oppRecoveredBy: new FormControl(null, [Validators.max(99)]),
        oppInterceptedBy: new FormControl(null, [Validators.max(99)]),
      });
      this.gamePlayNumber++;
    } else {
      this.gamePlayForm = new FormGroup({
        odk: new FormControl(this.editedGamePlay.odk, [Validators.required, this.isInODK]),
        down: new FormControl(this.editedGamePlay.down, [Validators.required, this.isInDown]),
        distance: new FormControl(this.editedGamePlay.distance, [Validators.required]),
        yardLine: new FormControl(this.editedGamePlay.yardLine, [Validators.required]),
        hash: new FormControl(this.editedGamePlay.hash, [Validators.required, this.isInHash]),
        qtr: new FormControl(this.editedGamePlay.qtr, [Validators.required]),
        playType: new FormControl(this.editedGamePlay.playType, [Validators.required, this.isInPlayType]),
        result: new FormControl(this.editedGamePlay.result, [Validators.required, this.isInPlayResult]),
        gnLs: new FormControl(this.editedGamePlay.gnLs),
        penTeam: new FormControl(this.editedGamePlay.penTeam),
        penYards: new FormControl(this.editedGamePlay.penYards),
        kickYards: new FormControl(this.editedGamePlay.kickYards),
        returnYards: new FormControl(this.editedGamePlay.returnYards),
        rusher: new FormControl(this.editedGamePlay.rusher, [Validators.max(99)]),
        passer: new FormControl(this.editedGamePlay.passer, [Validators.max(99)]),
        receiver: new FormControl(this.editedGamePlay.receiver, [Validators.max(99)]),
        tackler1: new FormControl(this.editedGamePlay.tackler1, [Validators.max(99)]),
        tackler2: new FormControl(this.editedGamePlay.tackler2, [Validators.max(99)]),
        battedDown: new FormControl(this.editedGamePlay.battedDown, [Validators.max(99)]),
        forcedFumbleBy: new FormControl(this.editedGamePlay.forcedFumbleBy, [Validators.max(99)]),
        returner: new FormControl(this.editedGamePlay.returner, [Validators.max(99)]),
        kicker: new FormControl(this.editedGamePlay.kicker, [Validators.max(99)]),
        recoveredBy: new FormControl(this.editedGamePlay.recoveredBy, [Validators.max(99)]),
        interceptedBy: new FormControl(this.editedGamePlay.interceptedBy, [Validators.max(99)]),
        oppRusher: new FormControl(this.editedGamePlay.oppRusher, [Validators.max(99)]),
        oppPasser: new FormControl(this.editedGamePlay.oppPasser, [Validators.max(99)]),
        oppReceiver: new FormControl(this.editedGamePlay.oppReceiver, [Validators.max(99)]),
        oppTackler1: new FormControl(this.editedGamePlay.oppTackler1, [Validators.max(99)]),
        oppTackler2: new FormControl(this.editedGamePlay.oppTackler2, [Validators.max(99)]),
        oppBattedDown: new FormControl(this.editedGamePlay.oppBattedDown, [Validators.max(99)]),
        oppForcedFumbleBy: new FormControl(this.editedGamePlay.oppForcedFumbleBy, [Validators.max(99)]),
        oppReturner: new FormControl(this.editedGamePlay.oppReturner, [Validators.max(99)]),
        oppKicker: new FormControl(this.editedGamePlay.oppKicker, [Validators.max(99)]),
        oppRecoveredBy: new FormControl(this.editedGamePlay.oppRecoveredBy, [Validators.max(99)]),
        oppInterceptedBy: new FormControl(this.editedGamePlay.oppInterceptedBy, [Validators.max(99)]),
      });
    }
  }

  public getParamGamePlayID() {
    return this.route.snapshot.paramMap.get('id')!;
  }

  save() {
    if (!this.isFormValid()) {
      return;
    }

    if (this.newPlay) {
      const gamePlay: GamePlay = this.gamePlayForm!.value;
      this.gamePlayService.insertNewPlayByPlay(this.game.game.id, gamePlay, this.gamePlayNumber).subscribe({
        next: () => {
          this.back();
          this.game.refreshGamePlay();
          this.showSaveConfirmation();
        },
        error: error => this.showSaveError(error)
      });
    } else {
      const gamePlay = this.game.gamePlayList.find(gp => gp.gamePlayNumber === this.gamePlayNumber);
      if (!gamePlay) {
        this.messageDialog.openMessage(
          'validation.errorTitle',
          'validation.errorUnableToSavePlay',
          'app.ok',
          'error',
        );
        return;
      }
      Object.assign(gamePlay, this.gamePlayForm!.value);
      // Même mécanisme de sauvegarde que l'édition en ligne du tableau
      // (play-list.component.ts#saveRow) : passe par le worker/IndexedDB.
      this.game.saveExistingGamePlay(gamePlay);
      this.back();
      this.showSaveConfirmation();
    }
  }

  private showSaveConfirmation() {
    const messageConfirmation = this.translocoService.translate('components.gamePlayEdit.saveConfirmation');
    this._snackBar.open(messageConfirmation, '', {
      horizontalPosition: 'right',
      duration: 3000
    });
  }

  private showSaveError(error: any) {
    captureExceptionWithTags(error, { gameId: this.game.game?.id, gamePlayNumber: this.gamePlayNumber });
    const messageKey = (error.status === 409)
      ? 'components.gamePlayEdit.erreur409'
      : 'components.gamePlayEdit.erreur';
    this.messageDialog.openMessage(
      'validation.errorTitle',
      messageKey,
      'app.ok',
      'error',
    );
  }

  cancel() {
    this.back();
  }

  back() {
    this.location.back();
  }

  openPlayerPicker(controlKey: string, team: 'home' | 'away') {
    const players = this.game.lineUp[team].fullTeam
      .map(player => player.jerseyNumber)
      .sort((a, b) => a - b);

    this.dialog.open(DialogSelectPlayerComponent, {
      data: { players }
    }).afterClosed().subscribe((selectedPlayer: number | undefined) => {
      if (selectedPlayer !== undefined) {
        this.gamePlayForm?.get(controlKey)?.setValue(selectedPlayer);
      }
    });
  }

  isRoleVisible(role: PlayerRole): boolean {
    const playType: PlayType = this.gamePlayForm?.value.playType;
    const result: PlayResult = this.gamePlayForm?.value.result;
    return ROLE_VISIBILITY[role.key](playType, result);
  }

  isFormValid(): boolean {
    if (this.gamePending()) {
      return true;
    }

    const controlsWithErrors = this.getControlsWithErrors(this.gamePlayForm!);
    if (controlsWithErrors.some(control => control.errors)) {
      console.log('Erreurs', controlsWithErrors.map(control => control.errors));
      this.messageDialog.openMessage(
        'validation.errorTitle',
        'validation.errorFormNotValid',
        'app.ok',
        'error',
      );
      return false;
    }

    const missingPrimaryPlayerMessage = this.getMissingPrimaryPlayerMessage();
    if (missingPrimaryPlayerMessage) {
      this.messageDialog.openMessage(
        'validation.errorTitle',
        missingPrimaryPlayerMessage,
        'app.ok',
        'error',
      );
      return false;
    }

    return true;
  }

  private getMissingPrimaryPlayerMessage(): string | null {
    const playType: PlayType = this.gamePlayForm!.value.playType;
    const result: PlayResult = this.gamePlayForm!.value.result;
    const odk: ODK = this.gamePlayForm!.value.odk;
    const requiredRole = PRIMARY_PLAYER_FIELD_BY_PLAY_TYPE[playType];
    if (!requiredRole) {
      return null;
    }

    // Le champ n'est pas toujours pertinent pour le Play Type (ex: qb-sack.component.ts
    // ne capture jamais de passeur) — voir ROLE_VISIBILITY, qui pilote aussi
    // l'affichage du champ dans le formulaire.
    if (!ROLE_VISIBILITY[requiredRole](playType, result)) {
      return null;
    }

    const fieldName = this.getRequiredFieldName(requiredRole, playType, odk);
    const value = this.gamePlayForm!.value[fieldName];
    if (value === null || value === undefined || value === '') {
      return REQUIRED_PLAYER_MESSAGE_KEY[requiredRole];
    }

    return null;
  }

  // rusher/passer vont sur le champ home ou opp selon quelle équipe a le ballon
  // (voir SetRusherCommand.ts/SetPasserCommand.ts) ; kicker va sur le champ opp
  // quand le playType représente l'équipe qui reçoit/bloque le botté (voir
  // SetKickerCommand.ts).
  private getRequiredFieldName(role: 'rusher' | 'passer' | 'kicker', playType: PlayType, odk: ODK): string {
    if (role === 'kicker') {
      return KICK_RECEIVING_PLAY_TYPES.includes(playType) ? 'oppKicker' : 'kicker';
    }
    return odk === ODK.Defensive ? `opp${role.charAt(0).toUpperCase()}${role.slice(1)}` : role;
  }

  gamePending(): boolean {
    const gamePlay = this.game.gamePlayList.find(gamePlay => gamePlay.gamePlayNumber === this.gamePlayNumber);
    if (!gamePlay) {
      return false;
    }
    return gamePlay._status === GameStatus.Pending;
  }

  getNumberOfDowns(): number {
    return this.game.getNumberOfDowns();
  }

  isInODK(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    if (Object.values(ODK).includes(control.value)) {
      return null;
    }
    return { notInODK: true };
  }

  isInDown(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    if (Object.values(Down).includes(parseInt(control.value))) {
      return null;
    }
    return { notInDown: true };
  }

  isInHash(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    if (Object.values(HashSimple).includes(control.value)) {
      return null;
    }
    return { notInHash: true };
  }

  isInPlayType(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    if (Object.values(PlayType).includes(control.value)) {
      return null;
    }
    return { notInPlayType: true };
  }

  isInPlayResult(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    if (Object.values(PlayResult).includes(control.value)) {
      return null;
    }
    return { notInPlayResult: true };
  }

  getControlsWithErrors(formGroup: FormGroup): AbstractControl[] {
    let controlsWithErrors: AbstractControl[] = [];

    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (!control) return;

      if (control instanceof FormGroup) {
        controlsWithErrors = controlsWithErrors.concat(this.getControlsWithErrors(control));
      } else if (control.errors) {
        controlsWithErrors.push(control);
      }
    });

    return controlsWithErrors;
  }
}
